import React, { useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import type { CommercialProduct, EconomicCalculation, Id, OriginType, PaymentMethod, Quote, StaffUser } from "../types";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "../router";
import { formatCurrency } from "../lib/format";

type PaymentCondition = {
  id: string | number;
  code: string;
  name: string;
  provider: string | null;
  payment_method: PaymentMethod;
  installments: number;
  card_brand: string | null;
  bank_name: string | null;
  promotion_name: string | null;
  fee_pct: number | string;
  fee_vat_pct: number | string;
  additional_pct: number | string;
  fixed_fee: number | string;
};

const PAYMENT_METHODS: { code: PaymentMethod; label: string }[] = [
  { code: "TRANSFER", label: "Transferencia" },
  { code: "CASH", label: "Efectivo" },
  { code: "CARD_1_PAYMENT", label: "Tarjeta 1 pago" },
  { code: "CARD_UP_TO_6", label: "Tarjeta hasta 6 cuotas" },
];

const ORIGIN_TYPES: { code: OriginType; label: string }[] = [
  { code: "COLD_LEAD_OR_CHAT_RESCUE", label: "Lead frío / rescate de chat" },
  { code: "POST_EVALUATION_RESCUE", label: "Rescate post-evaluación" },
  { code: "EXISTING_PATIENT_RESALE", label: "Reventa a paciente existente" },
  { code: "SELLER_SOURCED_PATIENT", label: "Paciente traído por vendedora" },
];

export default function QuoteBuilder({ opportunityId }: { opportunityId: Id }) {
  const { user, isAdminLike, hasPermission } = useAuth();

  const canViewInternalEconomics =
    hasPermission("economics.costs.view");

  const canApproveOverride =
    hasPermission("quotes.approve_override");

  const { show } = useToast();
  const navigate = useNavigate();

  const [products, setProducts] = useState<CommercialProduct[]>([]);
  const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [sellerChoice, setSellerChoice] = useState<string>("");

  const [productId, setProductId] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("TRANSFER");
  const [paymentConditionId, setPaymentConditionId] = useState<string>("");
  const [originType, setOriginType] = useState<OriginType>("COLD_LEAD_OR_CHAT_RESCUE");
  const [discountPct, setDiscountPct] = useState<string>("0");

  const [preview, setPreview] = useState<EconomicCalculation | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [overrideForId, setOverrideForId] = useState<Id | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overridePrice, setOverridePrice] = useState("");
  const [busyQuoteId, setBusyQuoteId] = useState<Id | null>(null);

  useEffect(() => {
    api.getCommercialProducts().then(setProducts).catch(() => {});
    api.getPaymentConditions({ active: true }).then(setPaymentConditions).catch(() => {});
    api.users().then(setUsers).catch(() => setUsers([]));
    reloadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  function reloadQuotes() {
    setLoadingQuotes(true);
    api
      .getQuotes({ opportunity_id: opportunityId })
      .then(setQuotes)
      .catch(() => setQuotes([]))
      .finally(() => setLoadingQuotes(false));
  }

  useEffect(() => {
    setPaymentConditionId("");
  }, [paymentMethod]);

  const discountRatio = useMemo(() => {
    const n = Number(discountPct);
    return Number.isFinite(n) ? Math.max(0, n) / 100 : 0;
  }, [discountPct]);

  const eligibleSellers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.active &&
          ["sales", "sales_manager"].includes(u.role_code)
      ),
    [users]
  );

  const sellerSelectionReady =
    user?.role_code === "sales" ||
    (isAdminLike && sellerChoice !== "");

  const effectiveSellerId: Id | undefined =
    user?.role_code === "sales"
      ? user.id
      : sellerChoice && sellerChoice !== "__institutional__"
        ? sellerChoice
        : undefined;

  function sellerName(id: Id | null | undefined) {
    if (id === null || id === undefined) {
      return "Venta institucional / sin vendedor";
    }

    if (String(user?.id) === String(id)) {
      return user?.full_name || `Usuario #${id}`;
    }

    return (
      users.find((u) => String(u.id) === String(id))?.full_name ||
      `Usuario #${id}`
    );
  }

  useEffect(() => {
    if (!productId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      api
        .calculateEconomics({
          product_id: productId,
          payment_method: paymentMethod,
          payment_condition_id: paymentConditionId || undefined,
          origin_type: originType,
          discount_pct: discountRatio,
          seller_id: effectiveSellerId,
        })
        .then((res) => {
          if (!cancelled) setPreview(res);
        })
        .catch((e) => {
          if (!cancelled) {
            setPreview(null);
            if (e instanceof ApiClientError) show(e.message, "error");
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, paymentMethod, paymentConditionId, originType, discountRatio, effectiveSellerId]);

  async function createQuote() {
    if (!productId) {
      show("Elegí un tratamiento.", "error");
      return;
    }

    if (!sellerSelectionReady) {
      show("Definí el responsable comercial o elegí venta institucional.", "error");
      return;
    }
    setCreating(true);
    try {
      await api.createQuote({
        opportunity_id: opportunityId,
        product_id: productId,
        payment_method: paymentMethod,
        payment_condition_id: paymentConditionId || undefined,
        origin_type: originType,
        discount_pct: discountRatio,
        seller_id: effectiveSellerId,
      });
      show("Presupuesto creado.", "success");
      reloadQuotes();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo crear el presupuesto.", "error");
    } finally {
      setCreating(false);
    }
  }

  async function sendQuote(id: Id) {
    setBusyQuoteId(id);
    try {
      await api.sendQuote(id);
      show("Presupuesto enviado.", "success");
      reloadQuotes();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo enviar el presupuesto.", "error");
    } finally {
      setBusyQuoteId(null);
    }
  }

  async function acceptQuote(id: Id) {
    setBusyQuoteId(id);
    try {
      const sale = await api.acceptQuote(id);
      show("Presupuesto aceptado. Venta creada.", "success");
      reloadQuotes();
      navigate(`/ventas/${sale.id}`);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo aceptar el presupuesto.", "error");
    } finally {
      setBusyQuoteId(null);
    }
  }

  async function submitOverride(id: Id) {
    if (!overrideReason.trim()) {
      show("Ingresá un motivo para el override.", "error");
      return;
    }
    setBusyQuoteId(id);
    try {
      await api.overrideQuote(id, {
        reason: overrideReason.trim(),
        new_final_price: overridePrice ? Number(overridePrice) : undefined,
      });
      show("Override registrado.", "success");
      setOverrideForId(null);
      setOverrideReason("");
      setOverridePrice("");
      reloadQuotes();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar el override.", "error");
    } finally {
      setBusyQuoteId(null);
    }
  }

  const selectedProduct = products.find((p) => String(p.id) === String(productId));

  return (
    <div className="quote-builder">
      <h3>Nuevo presupuesto</h3>
      <div className="form-grid">
        <label>Tratamiento</label>
        <select value={productId} onChange={(e: any) => setProductId(e.target.value)}>
          <option value="">Elegí un tratamiento...</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {formatCurrency(p.list_price)}
            </option>
          ))}
        </select>

        <label>Medio de pago</label>
        <select value={paymentMethod} onChange={(e: any) => setPaymentMethod(e.target.value)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </select>

        <label>Condición de cobro</label>
        <select
          value={paymentConditionId}
          onChange={(e: any) => setPaymentConditionId(e.target.value)}
        >
          <option value="">Condición estándar / histórica</option>
          {paymentConditions
            .filter((condition) => condition.payment_method === paymentMethod)
            .map((condition) => {
              const details = [
                condition.provider,
                condition.installments > 1
                  ? `${condition.installments} cuotas`
                  : "1 pago",
                condition.promotion_name,
                condition.bank_name,
                condition.card_brand,
              ].filter(Boolean);

              return (
                <option key={condition.id} value={String(condition.id)}>
                  {condition.name}
                  {details.length ? ` · ${details.join(" · ")}` : ""}
                </option>
              );
            })}
        </select>

        <label>Responsable comercial</label>
        {user?.role_code === "sales" ? (
          <div>
            <b>{user.full_name}</b>
            <div className="meta">Asignación automática a tu usuario.</div>
          </div>
        ) : (
          <select
            value={sellerChoice}
            onChange={(e: any) => setSellerChoice(e.target.value)}
          >
            <option value="">Seleccioná responsable...</option>
            <option value="__institutional__">
              Venta institucional / sin vendedor
            </option>
            {eligibleSellers.map((seller) => (
              <option key={seller.id} value={String(seller.id)}>
                {seller.full_name}
                {seller.role_code === "sales_manager" ? " · Responsable comercial" : ""}
              </option>
            ))}
          </select>
        )}

        <label>Origen comercial</label>
        <select value={originType} onChange={(e: any) => setOriginType(e.target.value)}>
          {ORIGIN_TYPES.map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </select>

        <label>Descuento (%)</label>
        <input type="number" min={0} max={100} value={discountPct} onChange={(e: any) => setDiscountPct(e.target.value)} />
      </div>

      {previewLoading && <p className="meta">Calculando...</p>}

      {preview && !previewLoading && (
        <div className={`guardrail-preview ${preview.override_required ? "bad" : "ok"}`}>
          <div className="guardrail-row">
            <span>Precio de lista</span>
            <b>{formatCurrency(preview.list_price)}</b>
          </div>
          <div className="guardrail-row">
            <span>Precio final</span>
            <b>{formatCurrency(preview.final_price)}</b>
          </div>
          {canViewInternalEconomics &&
            preview.protected_floor !== undefined && (
              <div className="guardrail-row">
                <span>Piso protegido</span>
                <b>{formatCurrency(preview.protected_floor)}</b>
              </div>
            )}
          <div className="guardrail-row">
            <span>Precio recomendado</span>
            <b>{formatCurrency(preview.recommended_price)}</b>
          </div>
          {canViewInternalEconomics && preview.bcm !== undefined && (
            <div className="guardrail-row">
              <span>BCM estimada</span>
              <b>{formatCurrency(preview.bcm)}</b>
            </div>
          )}
          {preview.seller_commission !== undefined && (
            <div className="guardrail-row">
              <span>Comisión estimada</span>
              <b>{formatCurrency(preview.seller_commission)}</b>
            </div>
          )}
          <div className="guardrail-status">
            {preview.override_required ? (
              <span className="pill pill-bad">REQUIERE AUTORIZACIÓN</span>
            ) : (
              <span className="pill pill-ok">HABILITADO</span>
            )}
          </div>
        </div>
      )}

      <button className="btn-primary" style={{ marginTop: 12 }} onClick={createQuote} disabled={!productId || creating || !sellerSelectionReady}>
        {creating ? "Creando..." : "Crear presupuesto"}
      </button>

      <h3 style={{ marginTop: 20 }}>Presupuestos de esta oportunidad</h3>
      {loadingQuotes && <p className="meta">Cargando...</p>}
      {!loadingQuotes && quotes.length === 0 && <p className="meta">Todavía no hay presupuestos.</p>}
      <div className="quote-list">
        {quotes.map((q) => {
          const product = products.find((p) => String(p.id) === String(q.product_id));
          const blocked = q.override_required && !q.override_approved_by;
          return (
            <div className="quote-item" key={q.id}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <b>{product?.name || `Producto #${q.product_id}`}</b>
                <span className={`pill ${blocked ? "pill-bad" : "pill-ok"}`}>{q.status}</span>
              </div>
              <div className="meta">
                Responsable: {sellerName(q.seller_id)}
                {" · "}
                Final: {formatCurrency(q.final_price)}
              {canViewInternalEconomics &&
                q.protected_floor !== undefined &&
                q.protected_floor !== null && (
                  <>
                    {" · "}
                    Piso: {formatCurrency(q.protected_floor)}
                  </>
                )}
                {q.override_required && (
                  <>
                    {" · "}
                    {q.override_approved_by ? (
                      <span className="pill pill-ok" style={{ marginLeft: 4 }}>
                        Override aprobado
                      </span>
                    ) : (
                      <span className="pill pill-bad" style={{ marginLeft: 4 }}>
                        Requiere override
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="row" style={{ marginTop: 8, gap: 8 }}>
                {["DRAFT", "NEGOTIATING"].includes(q.status) && (
                  <button className="btn-ghost" disabled={busyQuoteId === q.id || blocked} onClick={() => sendQuote(q.id)}>
                    Enviar
                  </button>
                )}
                {["SENT", "VIEWED", "NEGOTIATING"].includes(q.status) && (
                  <button className="btn-ghost" disabled={busyQuoteId === q.id || blocked} onClick={() => acceptQuote(q.id)}>
                    Aceptar
                  </button>
                )}
                {q.override_required && !q.override_approved_by && canApproveOverride && (
                  <button className="btn-ghost" onClick={() => setOverrideForId(overrideForId === q.id ? null : q.id)}>
                    {overrideForId === q.id ? "Cancelar override" : "Autorizar override"}
                  </button>
                )}
              </div>

              {overrideForId === q.id && (
                <div className="override-box">
                  <label>Motivo (requerido)</label>
                  <textarea rows={2} value={overrideReason} onChange={(e: any) => setOverrideReason(e.target.value)} />
                  <label>Nuevo precio final (opcional)</label>
                  <input type="number" value={overridePrice} onChange={(e: any) => setOverridePrice(e.target.value)} placeholder={String(q.final_price)} />
                  <button className="btn-primary" disabled={busyQuoteId === q.id} onClick={() => submitOverride(q.id)}>
                    Aprobar override
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
